import { BillingPeriod } from '@modules/product/billingPeriod';
import DomPurify from 'dompurify';
import snarkdown from 'snarkdown';
import { simpleFormatAmount } from 'helpers/forms/checkouts';
// A series of general purpose helper functions.
// ----- Functions ----- //
// Ascending comparison function for use with Array.prototype.sort.
function ascending(a, b) {
    return a - b;
}
// Converts a number to a given number of decimal places, default two.
function roundToDecimalPlaces(num, dps = 2) {
    return Math.round(num * 10 ** dps) / 10 ** dps;
}
// Generates the "class class-modifier" string for HTML elements.
// Does not add null, undefined and empty string.
function classNameWithModifiers(className, modifiers) {
    const validModifiers = modifiers.filter(Boolean);
    return validModifiers.reduce((acc, modifier) => `${acc} ${className}--${modifier}`, className);
}
// Deserialises a JSON object from a string.
function deserialiseJsonObject(serialised) {
    try {
        const deserialised = JSON.parse(serialised);
        if (deserialised instanceof Object && !(deserialised instanceof Array)) {
            return deserialised;
        }
        return null;
    }
    catch (err) {
        return null;
    }
}
function getSanitisedHtml(markdownString) {
    // ensure we don't accidentally inject dangerous html into the page
    return DomPurify.sanitize(snarkdown(markdownString), {
        ALLOWED_TAGS: ['em', 'strong', 'ul', 'li', 'a', 'p', 's'],
    });
}
// Automate days remaining copy where campaign has a countdown in the page
// relies on the countdown updating state in the 3 tier landing page component
const DEADLINE_PLACEHOLDER_TEMPLATE = '%%CAMPAIGN_DEADLINE%%';
function replaceDatePlaceholder(copy, deadline) {
    if (!copy.includes(DEADLINE_PLACEHOLDER_TEMPLATE)) {
        return copy;
    }
    if (!deadline) {
        return copy.replaceAll(DEADLINE_PLACEHOLDER_TEMPLATE, '');
    }
    const daysLeft = parseInt(deadline, 10);
    if (isNaN(daysLeft)) {
        return copy.replaceAll(DEADLINE_PLACEHOLDER_TEMPLATE, '');
    }
    const replacement = daysLeft === 0
        ? 'Final day'
        : daysLeft === 1
            ? '1 day left'
            : `${daysLeft} days left`;
    return copy.replaceAll(DEADLINE_PLACEHOLDER_TEMPLATE, replacement);
}
const mParticleAmountTemplate = /%%mParticle_([a-zA-Z0-9_]+)%%/g;
function replaceMParticleTemplates(copy, userAttributes, currency) {
    return copy.replaceAll(mParticleAmountTemplate, (template, attribute) => {
        const value = userAttributes[attribute];
        const amount = typeof value === 'number'
            ? value
            : typeof value === 'string' && value.trim() !== ''
                ? Number(value)
                : NaN;
        return Number.isFinite(amount)
            ? simpleFormatAmount(currency, amount)
            : template;
    });
}
// Parses a comma-separated string of amounts and returns an array of valid, unique numbers.
// Filters out invalid values (NaN, negative, zero, infinite) and removes duplicates.
function parseCustomAmounts(customAmountsParam) {
    return customAmountsParam
        .split(',')
        .map((amount) => parseFloat(amount.trim()))
        .filter((amount) => !isNaN(amount) && isFinite(amount) && amount > 0)
        .filter((amount, index, array) => array.indexOf(amount) === index);
}
function parseBillingPeriodCopy(copy, currency, price, billingPeriod) {
    const weeklyPrice = calculateWeeklyPrice(price, billingPeriod);
    const formattedWeeklyPrice = simpleFormatAmount(currency, weeklyPrice);
    return copy
        .replaceAll('%%PRICE_PRODUCT_WEEKLY%%', formattedWeeklyPrice)
        .trim();
}
function calculateWeeklyPrice(price, billingPeriod) {
    const pricePerDay = billingPeriod === BillingPeriod.Annual
        ? price / 365
        : billingPeriod === BillingPeriod.Quarterly
            ? (price * 4) / 365
            : billingPeriod === BillingPeriod.Monthly
                ? (price * 12) / 365
                : price;
    return pricePerDay * 7;
}
// ----- Exports ----- //
export { ascending, getSanitisedHtml, roundToDecimalPlaces, classNameWithModifiers, deserialiseJsonObject, parseCustomAmounts, parseBillingPeriodCopy, replaceDatePlaceholder, replaceMParticleTemplates, calculateWeeklyPrice, };
